import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  realtimeService,
  type EntityChangedEvent,
} from '@/services/realtime.service';
import { rootsFor } from '@/realtime/invalidation-map';

/**
 * How long to collect events before invalidating, in ms.
 *
 * A bulk action (import 300 patients, close a payroll period) arrives as
 * hundreds of frames within a second. Invalidating per frame would refetch
 * the patients list hundreds of times; collecting them turns that into one
 * refetch of each distinct root. 250 ms is below what anyone perceives as
 * "late" and above what a burst takes to land.
 */
const BATCH_WINDOW_MS = 250;

/**
 * Keeps the change feed open while the user has a clinic.
 *
 * Mount once, at the app root. Keyed on `clinicId` rather than on
 * `isAuthenticated`: a logged-in user with no clinic selected has no room
 * to join, and switching clinics re-issues the cookie, so the socket must
 * be re-opened to present the new one.
 *
 * On reconnect everything active is invalidated: the feed has no history,
 * so whatever changed while the socket was down is unknown, and refetching
 * what is on screen is the only honest recovery.
 */
export function useRealtimeConnection(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clinicId) {
      realtimeService.disconnect();
      return;
    }
    realtimeService.reconnect();
    const offReconnect = realtimeService.onReconnect(() => {
      void queryClient.invalidateQueries();
    });
    return () => {
      offReconnect();
      realtimeService.disconnect();
    };
  }, [clinicId, queryClient]);
}

/**
 * Turns "record X changed" into React Query invalidations.
 *
 * Mount once, at the app root. Every screen built on `useQuery` then
 * refreshes when someone else changes what it shows, with no per-screen
 * wiring: `invalidateQueries` refetches only queries that are mounted and
 * marks the rest stale for their next mount.
 *
 * This tab's own changes are skipped — its mutation's `onSuccess` has
 * already invalidated what it touched, and a second refetch would only
 * flicker. A second tab of the same user is a different client id and does
 * get the event, which is right: it is a different screen.
 */
export function useRealtimeInvalidation() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const pending = new Map<string, QueryKey>();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      timer = null;
      const roots = Array.from(pending.values());
      pending.clear();
      for (const queryKey of roots) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };

    const off = realtimeService.onEvent((event) => {
      if (realtimeService.isOwn(event)) return;
      for (const root of rootsFor(event)) {
        pending.set(JSON.stringify(root), root);
      }
      if (!timer) timer = setTimeout(flush, BATCH_WINDOW_MS);
    });

    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [queryClient]);
}

/**
 * Re-run a screen's own loader when one of the given entities changes.
 *
 * For screens that fetch by hand (`useEffect` + `useState`) rather than
 * through React Query, which the invalidation above cannot reach. Pass the
 * audit entity types the screen shows; pass `'*'` to refetch on anything.
 *
 * `refetch` is read through a ref so callers may pass the `useCallback`
 * they already have without the subscription churning on every identity
 * change. Events are batched the same way as invalidations, so a burst
 * costs one reload, and this tab's own changes are skipped for the same
 * reason.
 */
export function useRealtimeRefetch(
  entities: readonly string[] | '*',
  refetch: () => void | Promise<unknown>,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });
  const wanted = entities === '*' ? '*' : entities.join('|');

  useEffect(() => {
    if (!enabled) return;
    const set = wanted === '*' ? null : new Set(wanted.split('|'));
    let timer: ReturnType<typeof setTimeout> | null = null;

    const off = realtimeService.onEvent((event) => {
      if (realtimeService.isOwn(event)) return;
      if (set && !set.has(event.entity) && !matchesSubject(set, event)) return;
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        void refetchRef.current();
      }, BATCH_WINDOW_MS);
    });

    // Missed frames while offline are unknowable; reload on reconnect.
    const offReconnect = realtimeService.onReconnect(() => {
      void refetchRef.current();
    });

    return () => {
      off();
      offReconnect();
      if (timer) clearTimeout(timer);
    };
  }, [wanted, enabled]);
}

/** A patient screen also cares about a payment filed under its patient. */
function matchesSubject(set: Set<string>, event: EntityChangedEvent): boolean {
  return event.subjectType !== null && set.has(event.subjectType);
}

/**
 * Raw access to the feed for a component with its own idea of what to do
 * with an event — a "someone else edited this" banner on an open form, say.
 * The listener is read through a ref, so an inline function is fine.
 */
export function useRealtimeEvent(
  listener: (event: EntityChangedEvent) => void,
) {
  const ref = useRef(listener);
  useEffect(() => {
    ref.current = listener;
  });
  useEffect(() => realtimeService.onEvent((event) => ref.current(event)), []);
}

const subscribeStatus = (onChange: () => void) =>
  realtimeService.onStatusChange(onChange);
const getStatus = () => realtimeService.isConnected();

/** Whether the change feed is currently connected — for a "live" indicator. */
export function useRealtimeStatus(): boolean {
  return useSyncExternalStore(subscribeStatus, getStatus, () => false);
}
